import { DimensionColumn } from "@blisspointmedia/bpm-types/dist/MetricsTable";
import { METRIC_TO_PRETTY_NAME } from "./crossChannelConstants";
import * as CC from "@blisspointmedia/bpm-types/dist/CrossChannelPerformance";
import * as Dfns from "date-fns/fp";
import * as DfnsTZ from "date-fns-tz/fp";
import * as R from "ramda";
import {
  Metric,
  NativeCurrencyMetric,
  Metrics,
  CrossChannelMetrics,
  MetricTotalsByDate,
  KpiData,
} from "@blisspointmedia/bpm-types/dist/CrossChannel";
import {
  CDN,
  CellData,
  ColumnMetaData,
  DimensionMap,
} from "../SingleChannel/MetricsTable/metricsTableUtils";
import {
  getCompanyChannelColumnMetaData,
  sharedColumnMetaDataMap,
  sharedDimensionColumnMetaDataMap,
} from "../SingleChannel/Channels/sharedPerformanceUtils";

const {
  CLICKS,
  CLICKS_CONVERSION_RATE,
  CPC,
  CPM,
  CPX,
  CTR,
  IMPS_CONVERSION_RATE,
  IMPRESSIONS,
  REVENUE,
  ROAS,
  SPEND,
  VOLUME,
  VIDEO_VIEWS,
  VIDEOS_WATCHED_AT_25P,
  VIDEOS_WATCHED_AT_50P,
  VIDEOS_WATCHED_AT_75P,
  VIDEOS_WATCHED_AT_100P,
} = Metric;

const { CPC_NATIVE, CPM_NATIVE, CPX_NATIVE, SPEND_NATIVE, REVENUE_NATIVE } = NativeCurrencyMetric;
const {
  [SPEND_NATIVE]: spendNativeMetaData,
  [REVENUE_NATIVE]: revenueNativeMetaData,
  [CPX_NATIVE]: cpxNativeMetaData,
  [CPM_NATIVE]: cpmNativeMetaData,
  [CPC_NATIVE]: cpcNativeMetaData,
  [VIDEO_VIEWS]: videoViewsMetaData,
  [VIDEOS_WATCHED_AT_25P]: videosWatchedAt25pMetaData,
  [VIDEOS_WATCHED_AT_50P]: videosWatchedAt50pMetaData,
  [VIDEOS_WATCHED_AT_75P]: videosWatchedAt75pMetaData,
  [VIDEOS_WATCHED_AT_100P]: videosWatchedAt100pMetaData,
  [CLICKS]: clicksMetaData,
  [IMPRESSIONS]: impressionsMetaData,
  [SPEND]: spendMetaData,
  [REVENUE]: revenueMetaData,
  [ROAS]: roasMetaData,
  [VOLUME]: volumeMetaData,
  [CPM]: cpmMetaData,
  [CPX]: cpxMetaData,
  [CTR]: ctrMetaData,
  [CPC]: cpcMetaData,
  [CLICKS_CONVERSION_RATE]: clicksConversionRateMetaData,
  [IMPS_CONVERSION_RATE]: impsConversionRateMetaData,
} = sharedColumnMetaDataMap;

const DAY_DATE_LABEL = "M/dd/yy";
const formatDateLabel = (date: string): string => {
  if (!date) {
    return "";
  }

  const toUTC = DfnsTZ.utcToZonedTime("UTC", new Date(date));
  const isValid = Dfns.isValid(toUTC);

  if (!isValid) {
    return "";
  }

  const formattedDate = Dfns.format(DAY_DATE_LABEL, toUTC);

  return formattedDate;
};

// TODO consolidate with date-utils funcs
export const makePrettyDateRange = (start: string, end: string): string => {
  return `${formatDateLabel(start)}–${formatDateLabel(end)}`;
};

/**
 * Get default date ranges for date picker.
 */
export const getDefaultDates = (): {
  start: string;
  end: string;
  otherStart: string;
  otherEnd: string;
} => {
  // TODO: Remove after demos.
  const start = "2024-04-01";
  const end = "2024-04-30";
  const otherStart = "2024-03-01";
  const otherEnd = "2024-03-30";
  // const start = R.pipe(Dfns.subDays(31), Dfns.format(DATE_FORMAT))(new Date());
  // const end = R.pipe(Dfns.subDays(1), Dfns.format(DATE_FORMAT))(new Date());
  // const otherStart = R.pipe(Dfns.subDays(61), Dfns.format(DATE_FORMAT))(new Date());
  // const otherEnd = R.pipe(Dfns.subDays(32), Dfns.format(DATE_FORMAT))(new Date());
  return { start, end, otherStart, otherEnd };
};

/**
 * Aggregate platform delivery metrics by day for the Overview tab.
 */
export const calculateOverviewMetricsByDay = ({
  metricsData,
  kpiData,
  selectedKpi,
}: {
  metricsData: CrossChannelMetrics[];
  kpiData: KpiData[];
  selectedKpi: string | undefined;
}): MetricTotalsByDate[] => {
  if (R.isNil(metricsData) || R.isEmpty(metricsData)) {
    return [];
  }

  // Sum metric totals by day
  let metricsByDay: Record<string, Metrics> = {};
  for (let item of metricsData) {
    const { date, metrics } = item;
    metricsByDay[date] = R.mergeWith(R.add, metricsByDay[date] || {}, metrics);
  }

  // Calculate derived metrics by day
  const allMetricsByDay = Object.entries(metricsByDay).map(([date, metrics]) => {
    const spend = R.pathOr(0, [date, SPEND], metricsByDay);
    const clicks = R.pathOr(0, [date, CLICKS], metricsByDay);
    const impressions = R.pathOr(0, [date, IMPRESSIONS], metricsByDay);
    const volume = R.pathOr(0, [date, VOLUME], metricsByDay);
    const revenue = R.pathOr(0, [date, REVENUE], metricsByDay);

    let computedMetrics = {};
    computedMetrics[CPC] = calculateCPC(spend, clicks);
    computedMetrics[CPM] = calculateCPM(spend, impressions);
    computedMetrics[CTR] = calculateCTR(clicks, impressions);
    computedMetrics[CPX] = calculateCPX(spend, volume);
    computedMetrics[ROAS] = calculateROAS(revenue, spend);
    computedMetrics[CLICKS_CONVERSION_RATE] = calculateCCR(volume, clicks);
    computedMetrics[IMPS_CONVERSION_RATE] = calculateICR(volume, impressions);

    return { date, metrics: { ...metrics, ...computedMetrics } };
  });

  return allMetricsByDay.sort((a, b) => a.date.localeCompare(b.date));
};

/**
 * Calculate Cost-Per-Click (CPC).
 */
export const calculateCPC = (spend: number, clicks: number): number => {
  return clicks ? spend / clicks : 0;
};

/**
 * Calculate Cost-Per-Thousand Impressions (CPM).
 */
export const calculateCPM = (spend: number, impressions: number): number => {
  return impressions ? (spend / impressions) * 1000 : 0;
};

/**
 * Calculate Cost-Per-X. X is the selected KPI (CPX)
 */
export const calculateCPX = (spend: number, volume: number): number => {
  return volume ? spend / volume : 0;
};

/**
 * Calculate Return on Ad Spend (ROAS)
 */
export const calculateROAS = (revenue: number, spend: number): number => {
  return spend ? revenue / spend : 0;
};

/**
 * Calculate Click Through Rate (CTR)
 */
export const calculateCTR = (clicks: number, impressions: number): number => {
  return impressions ? clicks / impressions : 0;
};

/**
 * Calculate Clicks Conversion Rate (CR)
 */
export const calculateCCR = (volume: number, clicks: number): number => {
  return clicks ? volume / clicks : 0;
};

/**
 * Calculate Impressions Conversion Rate (CR)
 */
export const calculateICR = (volume: number, impressions: number): number => {
  return impressions ? volume / impressions : 0;
};

export const getPrettyMetricName = (metric: string): string => {
  return METRIC_TO_PRETTY_NAME[metric] || metric;
};

export const makeColumnMetaData = (
  metric: CC.DimensionColumnType,
  iconStyle?: "logo" | "thumbnail" | undefined
): ColumnMetaData => {
  return {
    contentType: "text",
    dimensionVarName: metric,
    displayName: metric,
    iconStyle: iconStyle || undefined,
  };
};

export const dimensionColumnMetaDataMap: Partial<
  Record<CC.DimensionColumnType | string, ColumnMetaData>
> = {
  Platform: makeColumnMetaData("Platform", "logo"),
  Channel: makeColumnMetaData("Channel", "logo"),
  "Account Name": makeColumnMetaData("Account Name"),
  "Account ID": makeColumnMetaData("Account ID"),
  Surface: makeColumnMetaData("Surface"),
  "Campaign Name": makeColumnMetaData("Campaign Name"),
  "Campaign ID": makeColumnMetaData("Campaign ID"),
  ...sharedDimensionColumnMetaDataMap,
};

export const columnMetaDataMap: Partial<Record<CC.ColumnType, ColumnMetaData>> = {
  [CLICKS]: clicksMetaData,
  [SPEND]: spendMetaData,
  [IMPRESSIONS]: impressionsMetaData,
  [REVENUE]: revenueMetaData,
  [ROAS]: roasMetaData,
  [VOLUME]: volumeMetaData,
  [CPM]: cpmMetaData,
  [CPX]: cpxMetaData,
  [CLICKS_CONVERSION_RATE]: clicksConversionRateMetaData,
  [IMPS_CONVERSION_RATE]: impsConversionRateMetaData,
  [CTR]: ctrMetaData,
  [CPC]: cpcMetaData,
  [SPEND_NATIVE]: spendNativeMetaData,
  [REVENUE_NATIVE]: revenueNativeMetaData,
  [CPC_NATIVE]: cpcNativeMetaData,
  [CPM_NATIVE]: cpmNativeMetaData,
  [CPX_NATIVE]: cpxNativeMetaData,
  [VIDEO_VIEWS]: videoViewsMetaData,
  [VIDEOS_WATCHED_AT_25P]: videosWatchedAt25pMetaData,
  [VIDEOS_WATCHED_AT_50P]: videosWatchedAt50pMetaData,
  [VIDEOS_WATCHED_AT_75P]: videosWatchedAt75pMetaData,
  [VIDEOS_WATCHED_AT_100P]: videosWatchedAt100pMetaData,
};

export const getCrossChannelDimensionCell = (
  dimensionData: DimensionMap,
  dimensionHeader: DimensionColumn
): CellData => {
  const { dimensionVarName, dimensionTypeName, icon } = dimensionHeader;
  const resolvedDimensionData = dimensionData as Record<string, string>;
  const dimensionValue = resolvedDimensionData[dimensionVarName];
  const defaultCell = {
    label: dimensionValue,
    value: dimensionValue,
  };
  if (dimensionTypeName === "Channel") {
    const parsedDimensionValue = dimensionValue.replace(/\s+/g, "");
    const url = !R.isNil(icon)
      ? `${CDN}/assets/img/channels/${parsedDimensionValue}.png`
      : undefined;
    return {
      ...defaultCell,
      url,
    };
  } else if (dimensionTypeName === "Platform") {
    const channelMap = {
      audio: "Streaming Audio",
      linear: "Linear TV",
      streaming: "Streaming Video",
    };
    const prettyNameMap = {
      audio: "Streaming Audio",
      facebook: "Meta",
      google_ads: "Google Ads",
      linear: "Linear TV",
      streaming: "Streaming Video",
      tiktok: "TikTok",
      twitter: "X",
    };
    const prettyName = prettyNameMap[dimensionValue.toLowerCase()]
      ? prettyNameMap[dimensionValue.toLowerCase()]
      : `${dimensionValue.replace(/_/g, " ").charAt(0).toUpperCase()}${dimensionValue
          .replace(/_/g, " ")
          .slice(1)}`;
    const url = !R.isNil(icon)
      ? `${CDN}/assets/img/${
          channelMap[dimensionValue.toLowerCase()] ? "channels" : "platforms"
        }/${prettyName.replace(/ /g, "")}.png`
      : undefined;
    return {
      label: prettyName,
      value: prettyName,
      url,
    };
  }

  return defaultCell;
};

export const getCrossChannelColumnMetaData = (
  company: string
): Partial<Record<CC.ColumnType, ColumnMetaData>> => {
  return getCompanyChannelColumnMetaData(columnMetaDataMap, company);
};

/**
 * SPARC is a portfolio of companies, and there are cases when they need custom behavior.
 */
export const isSparcCompany = (company: string): boolean =>
  ["reebok", "nautica", "eddiebauer", "lucky", "aeropostale", "brooksbrothers"].includes(company);
